import { useContext } from 'react';
import { Outlet, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { SidebarProvider, SidebarInset, SidebarTrigger } from '@/components/ui/sidebar';
import AppSidebar from '@/components/AppSidebar';
import TopBar from '@/components/TopBar';
import { Toaster } from '@/components/ui/sonner';
import { useKeyboardShortcuts } from '@/components/ui/KeyboardShortcuts';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Loading } from '@/components/ui/loading';
import { BreadcrumbNameContext } from '@/contexts/BreadcrumbNameContext';
import ProfileEditModal from '@/components/administracao/ProfileEditModal';
import React from 'react';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/components/ui/use-toast';
import { ConnectionStatus } from '@/components/ui/ConnectionStatus';
import { getMfaRequirement, getPreferredTotpFactor } from '@/lib/mfa';

const AppLayoutSecure = () => {
  const { user, loading } = useAuth();
  useKeyboardShortcuts();
  const { breadcrumbName } = useContext(BreadcrumbNameContext);
  const [profileModalOpen, setProfileModalOpen] = React.useState(false);
  const location = useLocation();
  const { toast } = useToast();
  const [aalChecked, setAalChecked] = React.useState(false);
  const [requiresMfa, setRequiresMfa] = React.useState(false);

  React.useEffect(() => {
    const handler = () => setProfileModalOpen(true);
    window.addEventListener('profile-modal-open', handler as EventListener);
    return () => window.removeEventListener('profile-modal-open', handler as EventListener);
  }, []);

  React.useEffect(() => {
    let mounted = true;

    const run = async () => {
      try {
        if (!user) {
          if (mounted) {
            setRequiresMfa(false);
            setAalChecked(true);
          }
          return;
        }

        const { data: aalData } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
        const { requiresMfa: needsMfa } = getMfaRequirement(aalData);

        if (mounted) {
          setRequiresMfa(needsMfa);
          setAalChecked(true);
        }

        if (!needsMfa) {
          return;
        }

        const factors = await supabase.auth.mfa.listFactors();
        const totpFactor = getPreferredTotpFactor(factors.data);

        if (!totpFactor) {
          toast({
            title: '2FA necessário',
            description:
              'Seu perfil exige 2FA, mas nenhum fator TOTP verificado está ativo. Ative o 2FA nas configurações do perfil.',
            variant: 'destructive',
          });
          await supabase.auth.signOut();
          window.location.replace('/login');
        }
      } catch (err) {
        console.error('Erro ao checar AAL no AppLayout:', err);
        if (mounted) {
          setRequiresMfa(false);
          setAalChecked(true);
        }
      }
    };

    run();
    return () => {
      mounted = false;
    };
  }, [toast, user]);

  if (loading || !aalChecked) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-gray-50 dark:bg-gray-900">
        <Loading size="lg" text="Carregando..." />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (requiresMfa) {
    return <Navigate to="/login" replace state={{ from: location, mfa: true }} />;
  }

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <header className="sticky top-0 z-50 flex h-16 shrink-0 items-center w-full px-0 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12">
          <div className="flex items-center w-full px-4 md:px-6 lg:px-8">
            <SidebarTrigger className="-ml-1 mr-2" />
            <div className="h-6 w-px bg-border mr-3" />
            <TopBar breadcrumbLastLabel={breadcrumbName} />
          </div>
        </header>
        <ScrollArea className="flex-1">
          <div className="w-full px-0 py-0">
            <main className="bg-background rounded-lg shadow-sm pt-4 px-4 md:px-6 lg:px-8 pb-4 md:pb-6 lg:pb-8 transition-colors">
              <Outlet />
            </main>
          </div>
        </ScrollArea>
      </SidebarInset>
      <Toaster />
      <ProfileEditModal open={profileModalOpen} onOpenChange={setProfileModalOpen} />
      <ConnectionStatus />
    </SidebarProvider>
  );
};

export default AppLayoutSecure;
